// Validation patterns
const NAME_PATTERN = /^[A-Za-z\s]+$/;
const EMAIL_PATTERN = /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/;
const ZIP_CODE_PATTERN = /^\d{5}(-\d{4})?$/;

// Validation functions
export const validateName = (value, fieldName, isRequired = true) => {
  if (!value?.trim()) {
    return isRequired ? "This field is required" : "";
  }
  if (!NAME_PATTERN.test(value)) {
    return "Please enter only alphabetic characters";
  }
  return "";
};

export const validateCity = (value) => {
  if (!value?.trim()) {
    return "This field is required";
  }
  if (!NAME_PATTERN.test(value)) {
    return "Please enter a valid city name";
  }
  return "";
};

export const validateEmail = (value) => {
  if (!value?.trim()) {
    return "This field is required";
  }
  if (!EMAIL_PATTERN.test(value)) {
    return "Invalid email address";
  }
  return "";
};

export const validatePhone = (value) => {
  if (!value?.trim()) {
    return "This field is required";
  }
  const cleanedPhone = value.replace(/\D/g, '');
  if (cleanedPhone.length !== 10) {
    return "Phone number must be 10 digits";
  }
  return "";
};

export const validateRequiredField = (value, fieldName) => {
  if (!value?.toString().trim()) {
    return `${fieldName} is required`;
  }
  return "";
};

export const validateZipCode = (value) => {
  if (!value?.trim()) {
    return "This field is required";
  }
  if (!ZIP_CODE_PATTERN.test(value)) {
    return "Invalid zip code format (e.g., 12345 or 12345-6789)";
  }
  return "";
};

export const validateDateOfBirth = (value) => {
  if (!value?.trim()) return ""; // Only validate if a value is provided

  // Handle MM-DD-YYYY format
  const parts = value.split(/[-\/]/);
  if (parts.length !== 3) {
    return "Please enter date in MM-DD-YYYY format";
  }

  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  const currentDate = new Date();

  // Check for zeros in individual parts
  if (month === 0 || day === 0 || year === 0) {
    return "Please enter a valid date";
  }

  // Create date object (months are 0-based in JavaScript)
  const date = new Date(year, month - 1, day);

  // Validate month, day, and year
  if (month < 1 || month > 12) {
    return "Month must be between 1 and 12";
  }
  if (day < 1 || day > 31) {
    return "Day must be between 1 and 31";
  }
  if (year < 1900 || year > currentDate.getFullYear()) {
    return `Year must be between 1900 and ${currentDate.getFullYear()}`;
  }
  // Check if date is valid (handles invalid dates like Feb 31)
  if (date.getMonth() !== month - 1 || date.getDate() !== day || date.getFullYear() !== year) {
    return "Please enter a valid date";
  }
  // Check if date is in the future
  if (date > currentDate) {
    return "Date of birth cannot be in the future";
  }

  return "";
};

export const validateLoginPassword = (value, isUserLoggedIn = false) => {
  if (isUserLoggedIn) return "";
  if (!value?.trim()) {
    return "This field is required";
  }
  return "";
};

export const validatePassword = (value, isUserLoggedIn = false) => {
  if (isUserLoggedIn) return "";
  if (!value?.trim()) {
    return "This field is required";
  }

  const errors = [];
  if (value.length < 8) errors.push("at least 8 characters");
  if (!/(?=.*[a-z])/.test(value)) errors.push("one lowercase letter");
  if (!/(?=.*[A-Z])/.test(value)) errors.push("one uppercase letter");
  if (!/(?=.*\d)/.test(value)) errors.push("one number");
  if (!/(?=.*[!@#$%^&*])/.test(value)) errors.push("one special character (!@#$%^&*)");

  if (errors.length > 0) {
    return `Password must contain ${errors.join(", ")}`;
  }
  return "";
};

export const validateConfirmPassword = (password, confirmPassword, isUserLoggedIn = false) => {
  if (isUserLoggedIn) return "";
  if (!confirmPassword?.trim()) {
    return "This field is required";
  }
  if (confirmPassword !== password) {
    return "Passwords do not match";
  }
  return "";
};

// Main validation function for form fields
export const validateField = (name, value, formValues, isLogin = false) => {
  if (isLogin) {
    switch (name) {
      case "email":
        return validateEmail(value);
      case "password":
        return validatePassword(value, formValues.isUserLoggedIn);
      case "login_password":
        return validateLoginPassword(value, formValues.isUserLoggedIn);
      default:
        return "";
    }
  }

  switch (name) {
    case "firstName":
    case "middleName":
    case "lastName":
      return validateName(value, name, name !== "middleName");
    case "city":
      return validateCity(value);
    case "email":
      return validateEmail(value);
    case "phone":
      return validatePhone(value);
    case "address":
    case "state":
    case "affiliated":
      return validateRequiredField(value, name);
    case "zipCode":
      return validateZipCode(value);
    case "dob":
      return validateDateOfBirth(value);
    case "password":
      return validatePassword(value, formValues.isUserLoggedIn);
    case "confirmPassword":
      return validateConfirmPassword(formValues.password, value, formValues.isUserLoggedIn);
    case "termAndCond":
      return value ? "" : "You must accept the terms and conditions";
    default:
      return "";
  }
};

export const formatDateToYYYYMMDD = (dateString) => {
  if (!dateString) return '';
 
  // Convert from MM-DD-YYYY to YYYY-MM-DD
  const [month, day, year] = dateString.split('-');
  if (!month || !day || !year) return dateString;
 
  // Ensure month and day are padded with leading zeros
  const paddedMonth = month.padStart(2, '0');
  const paddedDay = day.padStart(2, '0');
 
  return `${year}-${paddedMonth}-${paddedDay}`;
};